import { useEffect, useState } from 'react';
import { marked } from 'marked';
import { api, ApiError, type DriveItem, type DriveMeta } from '../lib/api';
import { formatSize, viewerKindFor } from '../lib/format';

export function FileViewer({ item }: { item: DriveItem | null }) {
  const [meta, setMeta] = useState<DriveMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setMeta(null);
    setError(null);
    if (!item) return;
    setLoading(true);
    void (async () => {
      try {
        setMeta(await api.fileMeta(item.id));
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'failed to load file');
      } finally {
        setLoading(false);
      }
    })();
  }, [item]);

  if (!item) {
    return (
      <div className="empty">
        <p>Select a file to view.</p>
      </div>
    );
  }

  return (
    <div className="viewer-pane">
      <div className="viewer-bar">
        <div className="breadcrumb">
          {meta?.path.map((p, i) => (
            <span key={p.id}>
              {i > 0 && <span className="sep"> / </span>}
              <span className={i === meta.path.length - 1 ? 'crumb current' : 'crumb'}>{p.name}</span>
            </span>
          )) ?? <span className="crumb current">{item.name}</span>}
        </div>
        <div className="viewer-actions">
          {meta?.webViewLink && (
            <a className="btn btn-ghost small" href={meta.webViewLink} target="_blank" rel="noreferrer">
              Open in Drive ↗
            </a>
          )}
        </div>
      </div>

      <div className="viewer-body">
        {loading && <div className="empty"><p className="muted">Loading…</p></div>}
        {error && <div className="empty"><p className="error">{error}</p></div>}
        {meta && !error && <Render meta={meta} />}
      </div>
    </div>
  );
}

function Render({ meta }: { meta: DriveMeta }) {
  const kind = viewerKindFor(meta.mimeType, meta.name);

  switch (kind) {
    case 'html':
      return <HtmlView fileId={meta.id} />;
    case 'markdown':
      return <MarkdownView fileId={meta.id} />;
    case 'text':
      return <TextView fileId={meta.id} />;
    case 'image':
      return (
        <div className="media-wrap">
          <img className="media-img" src={api.contentUrl(meta.id)} alt={meta.name} />
        </div>
      );
    case 'pdf':
      return <iframe className="frame pdf" src={api.contentUrl(meta.id)} title={meta.name} />;
    case 'google':
      return (
        <div className="empty">
          <p>This is a Google {googleKind(meta.mimeType)} document.</p>
          {meta.webViewLink && (
            <a className="btn btn-primary" href={meta.webViewLink} target="_blank" rel="noreferrer">
              Open in Google ↗
            </a>
          )}
        </div>
      );
    default:
      return (
        <div className="empty">
          <p className="muted">
            {meta.mimeType}
            {meta.size !== undefined ? ` · ${formatSize(meta.size)}` : ''}
          </p>
          <a className="btn btn-primary" href={api.contentUrl(meta.id)} download={meta.name}>
            Download ↓
          </a>
        </div>
      );
  }
}

function googleKind(mime: string): string {
  if (mime.endsWith('document')) return 'Docs';
  if (mime.endsWith('spreadsheet')) return 'Sheets';
  if (mime.endsWith('presentation')) return 'Slides';
  return 'Workspace';
}

/** HTML rendered in a cross-origin-isolated sandboxed iframe (no same-origin →
 *  cannot touch the host app's cookies). */
function HtmlView({ fileId }: { fileId: string }) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setHtml(null);
    setError(null);
    void api
      .fileText(fileId)
      .then(setHtml)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'failed'));
  }, [fileId]);

  if (error) return <div className="empty"><p className="error">{error}</p></div>;
  if (html === null) return <div className="empty"><p className="muted">Loading…</p></div>;
  return (
    <iframe
      className="frame html"
      sandbox="allow-scripts allow-popups allow-forms"
      srcDoc={html}
      title="HTML preview"
      referrerPolicy="no-referrer"
    />
  );
}

/** Markdown → HTML, rendered inside a script-less sandboxed iframe. */
function MarkdownView({ fileId }: { fileId: string }) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setHtml(null);
    setError(null);
    void api
      .fileText(fileId)
      .then(async (md) => {
        const body = await marked.parse(md);
        setHtml(
          `<!doctype html><meta charset="utf-8"><style>body{font:14px -apple-system,system-ui,sans-serif;max-width:760px;margin:24px auto;padding:0 16px;color:#1f2328;line-height:1.6}img{max-width:100%}pre{background:#f6f7f9;padding:12px;border-radius:6px;overflow:auto}code{font-family:ui-monospace,monospace}table{border-collapse:collapse}td,th{border:1px solid #ddd;padding:6px 10px}</style>${body}`,
        );
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'failed'));
  }, [fileId]);

  if (error) return <div className="empty"><p className="error">{error}</p></div>;
  if (html === null) return <div className="empty"><p className="muted">Loading…</p></div>;
  return <iframe className="frame md" sandbox="" srcDoc={html} title="Markdown preview" />;
}

function TextView({ fileId }: { fileId: string }) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setText(null);
    setError(null);
    void api
      .fileText(fileId)
      .then(setText)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'failed'));
  }, [fileId]);

  if (error) return <div className="empty"><p className="error">{error}</p></div>;
  if (text === null) return <div className="empty"><p className="muted">Loading…</p></div>;
  return <pre className="text-view">{text}</pre>;
}
